import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Shield, Download, Clock, User, Activity, 
  FileJson, FileSpreadsheet, Loader2, CheckCircle,
  Monitor, LogIn, AlertTriangle, Key, Copy, Send
} from "lucide-react";
import toast from "react-hot-toast";

const API = window.location.origin + "/api";

export default function SecurityPage() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // حالة إعادة تعيين كلمة المرور
  const [resetEmail, setResetEmail] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      const [logsRes, historyRes] = await Promise.all([
        axios.get(`${API}/security/audit-logs?limit=20`),
        axios.get(`${API}/security/login-history?limit=10`)
      ]);
      setAuditLogs(logsRes.data);
      setLoginHistory(historyRes.data);
    } catch (error) {
      console.error("Error fetching security data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/all-data`);
      const data = response.data;
      
      // تحويل إلى JSON وتحميل
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hesabaty_backup_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`تم تصدير ${data.statistics.products_count} منتج و ${data.statistics.sales_count} عملية بيع`);
    } catch (error) {
      toast.error("خطأ في تصدير البيانات");
    } finally {
      setExporting(false);
    }
  };

  const handleExportFinancial = async () => {
    try {
      const response = await axios.get(`${API}/export/financial-summary`);
      const data = response.data;
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `financial_summary_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("تم تصدير الملخص المالي");
    } catch (error) {
      toast.error("خطأ في تصدير الملخص المالي");
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "login": return <LogIn className="w-4 h-4 text-emerald-500" />;
      case "data_export": return <Download className="w-4 h-4 text-blue-500" />;
      default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("ar-IQ");
  };

  // دالة إعادة تعيين كلمة المرور للعميل
  const handleAdminResetPassword = async () => {
    if (!resetEmail.trim()) {
      toast.error("أدخل إيميل العميل");
      return;
    }
    
    setResetting(true);
    setResetResult(null);
    
    try {
      const response = await axios.post(`${API}/password/admin-reset`, {
        user_email: resetEmail
      });
      
      setResetResult(response.data);
      toast.success("تم إعادة تعيين كلمة المرور بنجاح");
    } catch (error) {
      const message = error.response?.data?.detail || "حدث خطأ";
      toast.error(message);
    } finally {
      setResetting(false);
    }
  };

  // نسخ كلمة المرور
  const copyPassword = () => {
    if (resetResult?.temp_password) {
      navigator.clipboard.writeText(resetResult.temp_password);
      toast.success("تم نسخ كلمة المرور");
    }
  };

  // إرسال عبر واتساب
  const sendViaWhatsApp = () => {
    if (resetResult) {
      const message = `مرحباً ${resetResult.user_name}،\n\nتم إعادة تعيين كلمة المرور الخاصة بك في نظام نازاريو حساباتي.\n\nكلمة المرور المؤقتة: ${resetResult.temp_password}\n\nيرجى تغييرها بعد تسجيل الدخول.\n\nرابط التطبيق: https://nazario-hesabaty.com`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" dir="rtl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-emerald-500" />
          <h1 className="text-2xl font-bold text-white">الأمان والنسخ الاحتياطي</h1>
        </div>
        <a href="/security-advanced">
          <Button className="bg-emerald-600 hover:bg-emerald-700">
            <Shield className="w-4 h-4 me-2" />
            الأمان المتقدم (2FA)
          </Button>
        </a>
      </div>

      {/* تصدير البيانات */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-500" />
            تصدير ونسخ احتياطي
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400 text-sm">
            قم بتصدير بياناتك بشكل دوري للحفاظ عليها. يمكنك استعادتها في أي وقت.
          </p>
          
          <div className="grid sm:grid-cols-2 gap-3">
            <Button
              onClick={handleExportAll}
              disabled={exporting}
              className="bg-blue-600 hover:bg-blue-700 h-auto py-4"
            >
              {exporting ? (
                <Loader2 className="w-5 h-5 animate-spin me-2" />
              ) : (
                <FileJson className="w-5 h-5 me-2" />
              )}
              <div className="text-start">
                <div className="font-bold">تصدير جميع البيانات</div>
                <div className="text-xs opacity-80">المنتجات، العملاء، المبيعات...</div>
              </div>
            </Button>
            
            <Button
              onClick={handleExportFinancial}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 h-auto py-4"
            >
              <FileSpreadsheet className="w-5 h-5 me-2" />
              <div className="text-start">
                <div className="font-bold">الملخص المالي</div>
                <div className="text-xs opacity-80">الأرباح، الديون، الإحصائيات</div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* إعادة تعيين كلمة مرور العميل */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-500" />
            إعادة تعيين كلمة مرور عميل
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-slate-400 text-sm">
            إذا نسي عميل كلمة المرور، أدخل إيميله هنا وسيتم توليد كلمة مرور مؤقتة لإرسالها له.
          </p>
          
          <div className="flex gap-3">
            <Input
              type="email"
              placeholder="إيميل العميل"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="bg-slate-700/50 border-slate-600 text-white flex-1"
              dir="ltr"
            />
            <Button
              onClick={handleAdminResetPassword}
              disabled={resetting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Key className="w-4 h-4 me-2" />
                  إعادة تعيين
                </>
              )}
            </Button>
          </div>
          
          {/* نتيجة إعادة التعيين */}
          {resetResult && (
            <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle className="w-5 h-5" />
                <span className="font-bold">تم إعادة تعيين كلمة المرور بنجاح!</span>
              </div>
              
              <div className="text-slate-300">
                <p>العميل: <span className="text-white font-bold">{resetResult.user_name}</span></p>
                <p>الإيميل: <span className="text-white" dir="ltr">{resetResult.user_email}</span></p>
              </div>
              
              <div className="bg-slate-800 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <span className="text-slate-400 text-sm">كلمة المرور المؤقتة:</span>
                  <p className="text-2xl font-bold text-amber-400 tracking-wider" dir="ltr">
                    {resetResult.temp_password}
                  </p>
                </div>
                <Button
                  onClick={copyPassword}
                  variant="outline"
                  size="sm"
                  className="border-slate-600"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <Button
                onClick={sendViaWhatsApp}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <Send className="w-4 h-4 me-2" />
                إرسال عبر واتساب
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* سجل تسجيلات الدخول */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Monitor className="w-5 h-5 text-amber-500" />
            سجل تسجيلات الدخول
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loginHistory.length === 0 ? (
            <p className="text-slate-400 text-center py-4">لا توجد سجلات</p>
          ) : (
            <div className="space-y-2">
              {loginHistory.map((entry, index) => (
                <div
                  key={entry.id || index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    entry.success ? "bg-slate-900/50" : "bg-red-500/10 border border-red-500/30"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {entry.success ? (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    )}
                    <div>
                      <p className="text-white text-sm">
                        {entry.success ? "تسجيل دخول ناجح" : "محاولة فاشلة"}
                      </p>
                      <p className="text-slate-400 text-xs">
                        {entry.ip_address || "غير معروف"}
                        {entry.new_device && (
                          <span className="ms-2 text-amber-500">(جهاز جديد)</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <span className="text-slate-500 text-xs">
                    {formatDate(entry.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* سجل النشاطات */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-500" />
            سجل النشاطات
          </CardTitle>
        </CardHeader>
        <CardContent>
          {auditLogs.length === 0 ? (
            <p className="text-slate-400 text-center py-4">لا توجد نشاطات مسجلة</p>
          ) : (
            <div className="space-y-2">
              {auditLogs.map((log, index) => (
                <div
                  key={log.id || index}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50"
                >
                  <div className="flex items-center gap-3">
                    {getActionIcon(log.action)}
                    <div>
                      <p className="text-white text-sm">{log.details || log.action}</p>
                      <p className="text-slate-400 text-xs">
                        {log.ip_address || ""}
                      </p>
                    </div>
                  </div>
                  <span className="text-slate-500 text-xs">
                    {formatDate(log.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
